import React, { useContext, useState } from "react";
import moment from 'moment/moment';
import useAuth from "../../auth/useAuth";

interface ContextData {
  brands: number[];
  links: number[];
  sources: Source[];
  countries: string[];
  tags: number[];
  assigned: boolean;
  tlPlayers: boolean;
  notTlPlayers: boolean;
  includeUntagged: boolean;
  activityType: string;
  grain: string;
  startDate: Date;
  endDate: Date;
  formattedFilter: string;
  datesFilter: string;
  brandsFilter: string;
  sourceFilter: string;
  tlPlayersFilter: string;
  includeUntaggedFilter: string;
  activityTypeFilter: string;
  tagFilter: string;
  countryFilter: string;
  managerFilter: string;
  updateContext: (data: PartialContextData) => void;
}

export type Source = {
  id: string;
  linkParam: string;
}

export const defaultSources: Source[] = [
  { id: "Tagged", linkParam: "include_tracked_events" },
  { id: "Organic", linkParam: "include_organic_events" }
];

type PartialContextData = Partial<ContextData>;

function formatDate(date: Date) {
  return moment(date).format('YYYY-MM-DD');
}

function formatDates(startDate: Date, endDate: Date) {
  return `date_from=${formatDate(startDate)}&date_to=${formatDate(endDate)}`;
}

function formatBrands(selectedBrands: number[]) {
  return 'brand_ids=' + selectedBrands.join(',');
}

function formatLinks(selectedLinks: number[]) {
  return 'link_ids=' + selectedLinks.join(',');
}

function formatSources(selectedSources: Source[]) {
  return selectedSources.map((source) => `${source.linkParam}=true`).join('&');
}

function formatTlPlayers(tlPlayers: boolean, notTlPlayers: boolean) {
  if (tlPlayers && !notTlPlayers) return 'tl_players=true';
  if (!tlPlayers && notTlPlayers) return 'tl_players=false';
  return '';
}

function formatIncludeUntagged(includeUntagged: boolean) {
  return includeUntagged ? '' : 'include_untagged=false';
}

function formatActivityType(activityType: string) {
  return activityType ? 'activity_type=' + activityType : '';
}

function formatTags(selectedTags: number[]) {
  return selectedTags.length > 0 ? 'tag_ids=' + selectedTags.join(',') : '';
}

function formatCountries(selectedCountries: string[]) {
  return 'countries=' + selectedCountries.join(',');
}

function formatManagers(assigned: boolean) {
  return 'assigned=' + assigned;
}

const defaultValue: ContextData = {
  brands: [] as number[],
  links: [] as number[],
  sources: defaultSources,
  countries: [] as string[],
  tags: [] as number[],
  assigned: true,
  tlPlayers: true,
  notTlPlayers: true,
  includeUntagged: true,
  activityType: '',
  grain: 'monthly',
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  endDate: new Date(),
  formattedFilter: '',
  datesFilter: '',
  brandsFilter: '',
  sourceFilter: '',
  tlPlayersFilter: '',
  includeUntaggedFilter: '',
  activityTypeFilter: '',
  tagFilter: '',
  countryFilter: '',
  managerFilter: '',
  updateContext: () => {}
};

const formattedFilter = (defaultValue: any) => {
  return formatDates(defaultValue.startDate, defaultValue.endDate) +
      '&' + formatBrands(defaultValue.brands) +
      '&' + formatLinks(defaultValue.links) +
      '&' + formatSources(defaultValue.sources) +
      '&' + formatTlPlayers(defaultValue.tlPlayers, defaultValue.notTlPlayers) +
      '&' + formatIncludeUntagged(defaultValue.includeUntagged) +
      '&' + formatActivityType(defaultValue.activityType) +
      '&' + formatTags(defaultValue.tags) +
      '&' + formatCountries(defaultValue.countries) +
      '&' + formatManagers(defaultValue.assigned)
}

export const ReportContext = React.createContext({
  ...defaultValue,
  formattedFilter: formattedFilter(defaultValue),
  datesFilter: formatDates(defaultValue.startDate, defaultValue.endDate),
  brandsFilter: formatBrands(defaultValue.brands),
  sourceFilter: formatSources(defaultValue.sources),
  tlPlayersFilter: formatTlPlayers(defaultValue.tlPlayers, defaultValue.notTlPlayers),
  includeUntaggedFilter: formatIncludeUntagged(defaultValue.includeUntagged),
  activityTypeFilter: formatActivityType(defaultValue.activityType),
  tagFilter: formatTags(defaultValue.tags),
  countryFilter: formatCountries(defaultValue.countries),
  managerFilter: formatManagers(defaultValue.assigned)
});

export const useReportContext = () => useContext(ReportContext);

export const ReportContextProvider = ({ children }: { children: any }) => {
  const auth = useAuth();

  const [items, setItems] = useState<ContextData>({
    ...defaultValue,
    formattedFilter: formattedFilter(defaultValue),
    datesFilter: formatDates(defaultValue.startDate, defaultValue.endDate),
    brandsFilter: formatBrands(defaultValue.brands),
    sourceFilter: formatSources(defaultValue.sources),
    tlPlayersFilter: formatTlPlayers(defaultValue.tlPlayers, defaultValue.notTlPlayers),
    includeUntaggedFilter: formatIncludeUntagged(defaultValue.includeUntagged),
    activityTypeFilter: formatActivityType(defaultValue.activityType),
    tagFilter: formatTags(defaultValue.tags),
    countryFilter: formatCountries(defaultValue.countries),
    managerFilter: auth.isManager() ? formatManagers(defaultValue.assigned) : ''
  });

  const update = (newData: PartialContextData) => {
    const obj = {
      ...items,
      ...newData,
    };
    setItems({
      ...obj,
      formattedFilter: formattedFilter(obj),
      datesFilter: formatDates(obj.startDate, obj.endDate),
      brandsFilter: formatBrands(obj.brands),
      sourceFilter: formatSources(obj.sources),
      tlPlayersFilter: formatTlPlayers(obj.tlPlayers, obj.notTlPlayers),
      includeUntaggedFilter: formatIncludeUntagged(obj.includeUntagged),
      activityTypeFilter: formatActivityType(obj.activityType),
      tagFilter: formatTags(obj.tags),
      countryFilter: formatCountries(obj.countries),
      managerFilter: auth.isManager() ? formatManagers(obj.assigned) : '',
      updateContext: update,
    });
  };

  return (
    <>
      <ReportContext.Provider value={{ ...items, updateContext: update }}>
        {children}
      </ReportContext.Provider>
    </>
  );
};
